import type { ReactNode } from 'react';

import { useTabs } from 'minimal-shared/hooks';
import { useParams, useLocation, useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import { Tab, Tabs, Alert, Skeleton, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useComboCoveragesById } from 'src/api/hooks/useEmployerDataQueries';
import { SEARCH_RESTORE_QUERY_PARAM, SEARCH_RESTORE_QUERY_PARAM_VALUE } from 'src/pages/dashboard/advanced-search/constants';
import {
  getSearchOriginFromLocationState,
  getSearchRestoreSnapshotFromLocationState,
} from 'src/pages/dashboard/advanced-search/search-restore-state';

import { ActionButton } from 'src/sections/employer-data/shared/components/action-button';

import { ComboGeneral } from './tabs/combo-general';
import { ComboRatingHistory } from './tabs/combo-rating-history';
import DynamicBreadcrumbs from '../dynamic-breadcrumbs/dynamic-breadcrumbs';

// ----------------------------------------------------------------------

export function Combo() {
  const { comboId } = useParams<{ comboId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, error, isLoading } = useComboCoveragesById(Number(comboId));
  const restoreSnapshot = getSearchRestoreSnapshotFromLocationState(location.state);
  const searchOrigin = getSearchOriginFromLocationState(location.state);
  const canNavigateBackToSearch = searchOrigin === 'combo' && restoreSnapshot !== null;

  const ED_COMBO_TABS = [
    {
      value: 'general',
      label: 'General',
      component: data?.data ? <ComboGeneral comboSummary={data.data} /> : null,
    },
    {
      value: 'rating-history',
      label: 'Rating history',
      component: <ComboRatingHistory />,
    },
    { value: 'communications', label: 'Communications', component: <Box /> },
  ];

  const tabs = useTabs('general');

  const handleBackToSearchClick = () => {
    if (!restoreSnapshot) {
      return;
    }

    navigate(
      `${paths.dashboard.advanced_search}?tab=${restoreSnapshot.tabId}&${SEARCH_RESTORE_QUERY_PARAM}=${SEARCH_RESTORE_QUERY_PARAM_VALUE}`,
      {
        state: {
          searchTab: restoreSnapshot.tabId,
          searchRestoreSnapshot: restoreSnapshot,
        },
      }
    );
  };

  let content: ReactNode;

  if (isLoading) {
    content = (
      <>
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} data-testid="skeleton" />
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
      </>
    );
  } else if (error) {
    content = <Alert severity="error">Error: {error.message}</Alert>;
  } else if (!data) {
    content = <Alert severity="info">Server Error.</Alert>;
  } else {
    content = (
      <Box sx={{ mb: { xs: 3, md: 5 } }}>
        <Typography variant="h4">
          {data?.data?.controllingCoverage?.comboName || comboId}
        </Typography>
        <DynamicBreadcrumbs crumbStartPath="combo" />
        {canNavigateBackToSearch ? (
          <ActionButton
            label="Back to search"
            testId="combo-back-to-search"
            onClick={handleBackToSearchClick}
            icon="lucide:arrow-left"
            iconPosition="start"
            variant="text"
          />
        ) : null}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabs.value}
            onChange={tabs.onChange}
            aria-label="employer data combo tabs"
            data-testid="employer-data-combo-tabs"
          >
            {ED_COMBO_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        {ED_COMBO_TABS.map(
          (tab) => tab.value === tabs.value && <Box key={tab.value}>{tab.component}</Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {content}
    </Box>
  );
}
